import {isAutomatedBrowser} from '@github-ui/browser-detection/is-automated-browser'
import {isSyntheticPerfTest} from '@github-ui/browser-detection/is-synthetic-perf-test'
import {getUIComputeCohort, isLoggedIn} from '@github-ui/client-env'
import {getCPUBucket} from '@github-ui/cpu-bucket'
import {isFeatureEnabled, subscribeToFeatureFlagEvaluations} from '@github-ui/feature-flags'
import {bundler, isUIService} from '@github-ui/runtime-environment'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'
import {wasServerRendered} from '@github-ui/ssr-utils'
import {getCurrentReactAppName, getCurrentRouteId} from '@github-ui/stats-metadata'
import type {RumEvent, RumEventDomainContext, RumResourceEventDomainContext} from '@datadog/browser-rum'

import datadogWorkerUrl from './datadog-worker?worker&url'
import {getDatadogRumConfig} from './config'
import type {DatadogRumSdk, RumSdkLoader} from './types'

const REMOVED = '[removed]'
const ABSOLUTE_URL_PATTERN = /https?:\/\/[^\s)\]}>"']+/gi
const SELECTOR_URL_ATTRIBUTE_PATTERN = /\[(action|href|src)="([^"]+)"\]/g
const GRAPHQL_OPERATION_NAME_PATTERN = /^[_A-Za-z][_0-9A-Za-z]*$/
const PERSISTED_QUERY_ID_PATTERN = /^[A-Za-z0-9._-]+$/
const SERVER_FUNCTION_HASH_PATTERN = /^[a-f0-9]{16,}$/i
const NUMERIC_SEGMENT_PATTERN = /^\d+$/
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const MAX_GRAPHQL_METADATA_LENGTH = 256
const MAX_AUTOMATIC_ACTION_NAME_LENGTH = 100
const MAX_SAFE_FIXED_PATH_SEGMENT_LENGTH = 64
const SLOW_RESOURCE_DURATION_NS = 1_000_000_000
const UI_SERVICE_ROUTE_CHANGE_EVENT = 'datadog-rum:route-change'
const SAFE_FIXED_ROUTE_PREFIXES = new Set(['issues', 'notifications', 'organizations', 'pulls', 'search', 'settings'])
const DYNAMIC_SETTINGS_ROUTE_NAMES = new Set([
  'applications',
  'apps',
  'enterprises',
  'installations',
  'organizations',
  'repositories',
  'teams',
])
const REPOSITORY_ROUTE_NAMES = new Set([
  'actions',
  'blob',
  'branches',
  'checks',
  'commit',
  'commits',
  'compare',
  'contributors',
  'discussions',
  'forks',
  'graphs',
  'issues',
  'network',
  'projects',
  'pull',
  'pulls',
  'queue',
  'raw',
  'releases',
  'security',
  'settings',
  'stargazers',
  'tags',
  'tree',
  'watchers',
])

let initialized = false
let setupPromise: Promise<boolean> | undefined
let rumSdk: DatadogRumSdk | undefined
let lastViewKey: string | undefined
let pageShowListener: ((event: PageTransitionEvent) => void) | undefined
let routeChangeListener: EventListener | undefined
let softNavSuccessListener: (() => void) | undefined
let stopFeatureFlagEvaluationTracking: (() => void) | undefined

const defaultSdkLoader: RumSdkLoader = async () => {
  const [{datadogRum}, {reactPlugin}] = await Promise.all([
    import('@datadog/browser-rum'),
    import('@datadog/browser-rum-react'),
  ])
  return {sdk: datadogRum, reactPlugin}
}

function getReleaseVersion(): string {
  return document.head.querySelector<HTMLMetaElement>('meta[name="release"]')?.content ?? ''
}

function getProxyUrl(proxyUrl: string, path: string, parameters: string, subdomain?: string): string {
  const url = new URL(proxyUrl, window.location.origin)
  url.searchParams.set('ddforward', `${path}?${parameters}`)
  if (subdomain) url.searchParams.set('ddforwardSubdomain', subdomain)
  return url.toString()
}

function originOnly(url: string): string {
  try {
    return new URL(url, window.location.origin).origin
  } catch {
    return REMOVED
  }
}

function normalizedRepositoryPath(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean)
  const hasApiRepositoryPrefix = segments[0] === 'repos' && segments.length >= 3
  const routeIndex = hasApiRepositoryPrefix ? 3 : 2
  const routeName = segments[routeIndex]

  const normalized = hasApiRepositoryPrefix ? ['repos', 'OWNER', 'REPO'] : ['OWNER', 'REPO']
  if (hasApiRepositoryPrefix && !routeName) return `/${normalized.join('/')}`
  if (!routeName || !REPOSITORY_ROUTE_NAMES.has(routeName)) return undefined

  normalized.push(routeName)

  const remaining = segments.slice(routeIndex + 1)
  const replaceNumericSegment = (index: number) => {
    if (NUMERIC_SEGMENT_PATTERN.test(remaining[index] ?? '')) remaining[index] = 'NUM'
  }

  if (routeName === 'pull' || routeName === 'pulls' || routeName === 'issues' || routeName === 'discussions') {
    // `/pull/new/<branch>` embeds a branch name, which can contain slashes.
    if ((routeName === 'pull' || routeName === 'pulls') && remaining[0] === 'new' && remaining[1]) {
      return `/${[...normalized, 'new', 'BRANCH'].join('/')}`
    }
    replaceNumericSegment(0)
    if ((routeName === 'pull' || routeName === 'pulls') && remaining[1] === 'changes' && remaining[2]) {
      remaining[2] = 'SHA'
    }
  } else if (routeName === 'queue') {
    const queuePartialIndex = remaining.indexOf('queue_partial')
    if (queuePartialIndex >= 0) {
      const queueSuffix = remaining.slice(queuePartialIndex)
      if (NUMERIC_SEGMENT_PATTERN.test(queueSuffix[1] ?? '')) queueSuffix[1] = 'NUM'
      return `/${[...normalized, 'BRANCH', ...queueSuffix].join('/')}`
    }
    if (remaining.length) return `/${[...normalized, 'BRANCH'].join('/')}`
  } else if (routeName === 'blob' || routeName === 'tree' || routeName === 'raw') {
    return `/${[...normalized, 'REF', 'PATH'].join('/')}`
  } else if (routeName === 'commit') {
    return `/${[...normalized, 'SHA'].join('/')}`
  } else if (routeName === 'compare') {
    return `/${[...normalized, 'REFS'].join('/')}`
  } else if (routeName === 'actions') {
    if (remaining[0] === 'runs') {
      replaceNumericSegment(1)
      if (remaining[2] === 'job' || remaining[2] === 'jobs') replaceNumericSegment(3)
      if (remaining[2] === 'graph' && (remaining[3] === 'job' || remaining[3] === 'matrix')) {
        replaceNumericSegment(4)
      }
    } else if (remaining[0] === 'jobs') {
      replaceNumericSegment(1)
    } else if (remaining[0] === 'workflows' && remaining[1]) {
      remaining[1] = 'WORKFLOW'
    }
  } else if (routeName === 'settings' && remaining[0] === 'roles') {
    replaceNumericSegment(1)
  }

  return `/${[...normalized, ...remaining].join('/')}`
}

function normalizedServerFunctionPath(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean)
  const serverFunctionIndex = segments.indexOf('_serverFn')
  const functionId = serverFunctionIndex >= 0 ? segments[serverFunctionIndex + 1] : undefined
  if (!functionId) return undefined

  if (SERVER_FUNCTION_HASH_PATTERN.test(functionId)) segments[serverFunctionIndex + 1] = 'HASH'
  return `/${segments.join('/')}`
}

function normalizedFixedPath(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean)
  if (!segments[0] || !SAFE_FIXED_ROUTE_PREFIXES.has(segments[0])) return undefined
  const settingsRouteName = segments[1]

  return `/${segments
    .map((segment, index) => {
      if (index === 0) return segment
      if (NUMERIC_SEGMENT_PATTERN.test(segment)) return 'NUM'
      if (UUID_PATTERN.test(segment)) return 'ID'
      if (SERVER_FUNCTION_HASH_PATTERN.test(segment)) return 'HASH'
      if (segments[0] === 'organizations' && index === 1) return 'NAME'
      if (
        segments[0] === 'settings' &&
        index === 2 &&
        settingsRouteName &&
        DYNAMIC_SETTINGS_ROUTE_NAMES.has(settingsRouteName)
      ) {
        return 'NAME'
      }
      if (segment.length > MAX_SAFE_FIXED_PATH_SEGMENT_LENGTH) return 'TEXT'
      return segment
    })
    .join('/')}`
}

function sanitizedResourceUrl(url: string): string {
  try {
    const parsedUrl = new URL(url, window.location.origin)
    const isGitHubAssets = parsedUrl.hostname === 'githubassets.com' || parsedUrl.hostname.endsWith('.githubassets.com')
    const isSameOriginStaticAsset =
      parsedUrl.origin === window.location.origin &&
      (parsedUrl.pathname.startsWith('/assets/') || parsedUrl.pathname.startsWith('/assets-cdn/'))

    if (isGitHubAssets || isSameOriginStaticAsset) return `${parsedUrl.origin}${parsedUrl.pathname}`

    const isGitHubResourceHost =
      parsedUrl.origin === window.location.origin ||
      parsedUrl.hostname === 'github.com' ||
      parsedUrl.hostname === 'api.github.com'
    if (!isGitHubResourceHost) return parsedUrl.origin

    const normalizedPath =
      normalizedServerFunctionPath(parsedUrl.pathname) ??
      normalizedRepositoryPath(parsedUrl.pathname) ??
      normalizedFixedPath(parsedUrl.pathname)
    return normalizedPath ? `${parsedUrl.origin}${normalizedPath}` : parsedUrl.origin
  } catch {
    return REMOVED
  }
}

function sanitizeUrlsInText(value: string): string {
  return value.replace(ABSOLUTE_URL_PATTERN, url => sanitizedResourceUrl(url))
}

function hasDynamicUrlInComposedPathSelector(value: string): boolean {
  let hasDynamicUrl = false

  value.replace(SELECTOR_URL_ATTRIBUTE_PATTERN, (match, _attribute: string, escapedUrl: string) => {
    const url = escapedUrl.replaceAll('\\/', '/')
    const sanitizedUrl = sanitizedResourceUrl(url)
    const normalized = url.startsWith('/') ? new URL(sanitizedUrl, window.location.origin).pathname : sanitizedUrl
    if (normalized !== url) hasDynamicUrl = true
    return match
  })

  return hasDynamicUrl
}

function sanitizeErrorUrls(error: {message: string; stack?: string}): void {
  error.message = sanitizeUrlsInText(error.message)
  if (error.stack) error.stack = sanitizeUrlsInText(error.stack)
}

function redactUnsafeAutomaticActionName(event: Extract<RumEvent, {type: 'action'}>): boolean {
  const actionTarget = event.action.target
  const nameSource = event._dd?.action?.name_source
  const composedPathSelector = event._dd?.action?.target?.composed_path_selector
  const clickedElementSelector = composedPathSelector?.split(';', 1)[0]
  const isGenericContainer = clickedElementSelector === 'DIV'
  const isRepositoryPath = composedPathSelector?.includes('[data-testid="path-name-segment"]')
  const isIssueListItemTitle = composedPathSelector?.includes('[data-testid="issue-listitem-title-link"]')
  const isCommitRow = composedPathSelector?.includes('[data-testid="commit-row-item"]')
  const isFileTreeItem = composedPathSelector?.includes('PRIVATE_TreeView-item')
  const isDiffLine = composedPathSelector?.includes('TR.diff-line-row')
  const isRepositoryActionsPage = /^\/[^/]+\/[^/]+\/actions(?:\/|$)/.test(window.location.pathname)
  const isActionsNavigation =
    isRepositoryActionsPage && composedPathSelector?.includes('A[data-component="NavList\\.Item"]')
  const isWorkflowRunTitle =
    isRepositoryActionsPage &&
    composedPathSelector?.includes('[data-testid="listitem-title-link"]') &&
    composedPathSelector.includes('WorkflowRunListItem-module')
  const isAccountMenu = composedPathSelector?.includes('[data-testid="github-avatar"]')
  const isProfileAvatar =
    clickedElementSelector?.startsWith('IMG.avatar.avatar-user') && composedPathSelector?.includes('DIV.avatar-upload')

  if (actionTarget && isAccountMenu) {
    actionTarget.name = 'Account menu'
  } else if (actionTarget && isProfileAvatar) {
    actionTarget.name = 'Profile avatar'
  } else if (actionTarget && isIssueListItemTitle) {
    actionTarget.name = 'Issue'
  } else if (actionTarget && isCommitRow) {
    actionTarget.name = 'Commit'
  } else if (actionTarget && isFileTreeItem) {
    actionTarget.name = 'File tree item'
  } else if (actionTarget && isDiffLine) {
    actionTarget.name = 'Diff line'
  } else if (actionTarget && isActionsNavigation) {
    actionTarget.name = 'Actions navigation'
  } else if (actionTarget && isWorkflowRunTitle) {
    actionTarget.name = 'Workflow run'
  } else if (
    actionTarget?.name &&
    (isRepositoryPath ||
      (nameSource === 'text_content' &&
        (actionTarget.name.length > MAX_AUTOMATIC_ACTION_NAME_LENGTH || isGenericContainer)))
  ) {
    actionTarget.name = REMOVED
  }

  return !composedPathSelector || !hasDynamicUrlInComposedPathSelector(composedPathSelector)
}

function isGitHubGraphQlUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url, window.location.origin)
    return parsedUrl.origin === window.location.origin && parsedUrl.pathname === '/_graphql'
  } catch {
    return false
  }
}

function isAllowedTracingUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url, window.location.origin)
    // Raw repository routes redirect cross-origin, where tracing headers cause the CORS preflight to fail.
    const isRawRepositoryContent = /^\/[^/]+\/[^/]+\/raw(?:\/|$)/.test(parsedUrl.pathname)
    return parsedUrl.origin === window.location.origin && !isRawRepositoryContent
  } catch {
    return false
  }
}

function isResourceDomainContext(context: RumEventDomainContext): context is RumResourceEventDomainContext {
  return 'isManual' in context && context.isManual === false && context.isWebSocket === false
}

function isLowValueStaticResource(event: Extract<RumEvent, {type: 'resource'}>): boolean {
  const {resource} = event
  const isSuccessful = resource.status_code === undefined || resource.status_code < 400
  const isFast = resource.duration !== undefined && resource.duration < SLOW_RESOURCE_DURATION_NS
  const isLowValueType =
    resource.type === 'image' ||
    resource.type === 'media' ||
    (resource.type === 'font' && resource.delivery_type === 'cache')

  return isSuccessful && isFast && isLowValueType
}

function isBoundedGraphQlMetadata(value: unknown, pattern: RegExp): value is string {
  return typeof value === 'string' && value.length <= MAX_GRAPHQL_METADATA_LENGTH && pattern.test(value)
}

function getPersistedQueryMetadata(
  resourceUrl: string,
  context: RumEventDomainContext,
): {name: string; id: string} | undefined {
  if (!isGitHubGraphQlUrl(resourceUrl)) return undefined

  let payload: string | undefined
  if (isResourceDomainContext(context) && typeof context.requestInit?.body === 'string') {
    payload = context.requestInit.body
  } else {
    try {
      payload = new URL(resourceUrl, window.location.origin).searchParams.get('body') ?? undefined
    } catch {
      return undefined
    }
  }
  if (!payload) return undefined

  try {
    const parsed: unknown = JSON.parse(payload)
    if (!isRecord(parsed)) return undefined
    const {persistedQueryName, query} = parsed
    if (
      !isBoundedGraphQlMetadata(persistedQueryName, GRAPHQL_OPERATION_NAME_PATTERN) ||
      !isBoundedGraphQlMetadata(query, PERSISTED_QUERY_ID_PATTERN)
    ) {
      return undefined
    }
    return {name: persistedQueryName, id: query}
  } catch {
    return undefined
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function sanitizeFeatureFlagName(name: string): string {
  return name.replace(/[^a-zA-Z0-9_]/g, '_')
}

function getSanitizedViewName(): string {
  return (
    document.head.querySelector<HTMLMetaElement>('meta[data-datadog-rum-route-pattern]')?.content ??
    document.head.querySelector<HTMLMetaElement>('meta[name="analytics-location"]')?.content ??
    document.head.querySelector<HTMLMetaElement>('meta[name="route-pattern"]')?.content ??
    '/'
  )
}

function getRumViewContext() {
  const uiComputeCohort = getUIComputeCohort()

  return {
    appName: getCurrentReactAppName() || 'rails',
    routeId: getCurrentRouteId(),
    ...(bundler ? {bundler} : {}),
    ui: isUIService,
    uiService: Boolean(document.head.querySelector('meta[name="ui-service"][content="true"]')),
    ssr: wasServerRendered(),
    loggedIn: isLoggedIn(),
    staff: Boolean(document.head.querySelector<HTMLMetaElement>('meta[name="user-staff"]')?.content),
    ...(uiComputeCohort ? {uiComputeCohort} : {}),
    passThrough: document.head.querySelector<HTMLMetaElement>('meta[name="ui-pass-through"]')?.content === 'true',
    synthetic: isSyntheticPerfTest(),
    cpu: getCPUBucket(),
  }
}

function startCurrentView(sdk: DatadogRumSdk, force = false, providedName?: string): void {
  const name = providedName || getSanitizedViewName()
  const context = getRumViewContext()
  const {appName, routeId} = context
  const viewKey = `${window.location.origin}${window.location.pathname}:${name}:${appName}:${routeId ?? ''}`
  if (!force && viewKey === lastViewKey) return

  lastViewKey = viewKey
  sdk.startView({name, url: window.location.origin, context})
}

function startViewTracking(sdk: DatadogRumSdk): void {
  pageShowListener = event => {
    if (event.persisted) startCurrentView(sdk, true)
  }
  window.addEventListener('pageshow', pageShowListener)

  routeChangeListener = event => {
    const name = event instanceof CustomEvent && typeof event.detail?.name === 'string' ? event.detail.name : undefined
    startCurrentView(sdk, false, name)
  }
  document.addEventListener(UI_SERVICE_ROUTE_CHANGE_EVENT, routeChangeListener)

  softNavSuccessListener = () => startCurrentView(sdk)
  document.addEventListener(SOFT_NAV_STATE.SUCCESS, softNavSuccessListener)
}

function redactRumEvent(event: RumEvent, domainContext: RumEventDomainContext): boolean {
  event.view.name ||= '/'
  event.view.url = originOnly(event.view.url)
  if (event.view.referrer) event.view.referrer = originOnly(event.view.referrer)

  if (event.type === 'error') {
    if (event.error.causes?.length) return false
    sanitizeErrorUrls(event.error)
    if (event.error.handling_stack) event.error.handling_stack = sanitizeUrlsInText(event.error.handling_stack)
    if (event.error.resource) event.error.resource.url = sanitizedResourceUrl(event.error.resource.url)
  } else if (event.type === 'action') {
    if (!redactUnsafeAutomaticActionName(event)) return false
  } else if (event.type === 'resource') {
    if (isLowValueStaticResource(event)) return false
    const persistedQuery = getPersistedQueryMetadata(event.resource.url, domainContext)
    event.resource.url = sanitizedResourceUrl(event.resource.url)
    if (event.resource.graphql?.variables !== undefined) event.resource.graphql.variables = REMOVED
    if (persistedQuery) {
      event.context = {...event.context, github_graphql: persistedQuery}
    }
  } else if (event.type === 'long_task') {
    for (const script of event.long_task.scripts ?? []) {
      if (script.source_url) script.source_url = sanitizedResourceUrl(script.source_url)
      if (script.invoker) script.invoker = sanitizeUrlsInText(script.invoker)
    }
  } else if (event.type === 'view' && event.view.performance?.lcp?.resource_url) {
    event.view.performance.lcp.resource_url = sanitizedResourceUrl(event.view.performance.lcp.resource_url)
  }

  return true
}

async function startRum(loadSdk: RumSdkLoader): Promise<boolean> {
  const isBot = isAutomatedBrowser() && !isSyntheticPerfTest()
  if (typeof document === 'undefined' || !isFeatureEnabled('datadog_rum') || isBot) return false

  const config = getDatadogRumConfig()
  if (!config) return false

  try {
    const {sdk, reactPlugin} = await loadSdk()
    stopFeatureFlagEvaluationTracking = subscribeToFeatureFlagEvaluations((featureFlag, enabled) => {
      sdk.addFeatureFlagEvaluation(sanitizeFeatureFlagName(featureFlag), enabled)
    })
    sdk.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site,
      service: config.service,
      env: config.env,
      version: getReleaseVersion(),
      proxy: ({path, parameters, subdomain}) => getProxyUrl(config.proxyUrl, path, parameters, subdomain),
      workerUrl: datadogWorkerUrl,
      profilingSampleRate: config.profilingSampleRate,
      sessionSampleRate: config.sessionSampleRate,
      sessionReplaySampleRate: 0,
      enableExperimentalFeatures: ['feature_flags'],
      compressIntakeRequests: true,
      telemetrySampleRate: 0,
      defaultPrivacyLevel: 'mask-user-input',
      useSecureSessionCookie: window.location.protocol === 'https:',
      trackAnonymousUser: false,
      trackViewsManually: true,
      // This also emits immutable DOM selectors; keep rollout staff-only until Privacy approves broader collection.
      trackUserInteractions: true,
      enablePrivacyForActionName: true,
      trackResources: true,
      allowedTracingUrls: [{match: isAllowedTracingUrl, propagatorTypes: ['tracecontext']}],
      allowedGraphQlUrls: [
        {
          match: isGitHubGraphQlUrl,
          trackPayload: false,
          trackResponseErrors: false,
        },
      ],
      trackLongTasks: true,
      beforeSend: redactRumEvent,
      plugins: [reactPlugin({router: false})],
      silentMultipleInit: true,
    })
    // Manual view tracking delays RUM startup until this first view supplies its initial options.
    startCurrentView(sdk)
    startViewTracking(sdk)

    rumSdk = sdk
    initialized = true
    return true
  } catch (error) {
    stopFeatureFlagEvaluationTracking?.()
    stopFeatureFlagEvaluationTracking = undefined
    setTimeout(() => {
      throw error
    })
    return false
  }
}

export function setupDatadogRum(loadSdk: RumSdkLoader = defaultSdkLoader): Promise<boolean> {
  if (initialized) return Promise.resolve(true)
  setupPromise ??= startRum(loadSdk).finally(() => {
    setupPromise = undefined
  })
  return setupPromise
}

export async function addDatadogDurationVital(
  name: string,
  options: Parameters<DatadogRumSdk['addDurationVital']>[1],
): Promise<boolean> {
  if (!(await setupDatadogRum()) || !rumSdk) return false

  rumSdk.addDurationVital(name, options)
  return true
}

// The name of the view currently tracked by RUM, derived from the server-rendered route metadata.
// Consumers can read this to build a related name (e.g. a suffixed variant) for `setDatadogRumViewName`.
export function getDatadogRumViewName(): string {
  return getSanitizedViewName()
}

// Renames the current RUM view without starting a new one. Useful when a route's rendered content
// changes in a way that should be distinguished in RUM dashboards, without altering the URL or route.
export async function setDatadogRumViewName(name: string): Promise<boolean> {
  if (!(await setupDatadogRum()) || !rumSdk) return false

  rumSdk.setViewName(name)
  return true
}

export function resetDatadogRumForTesting(): void {
  if (pageShowListener) window.removeEventListener('pageshow', pageShowListener)
  if (routeChangeListener) document.removeEventListener(UI_SERVICE_ROUTE_CHANGE_EVENT, routeChangeListener)
  if (softNavSuccessListener) document.removeEventListener(SOFT_NAV_STATE.SUCCESS, softNavSuccessListener)
  stopFeatureFlagEvaluationTracking?.()
  initialized = false
  setupPromise = undefined
  rumSdk = undefined
  lastViewKey = undefined
  pageShowListener = undefined
  routeChangeListener = undefined
  softNavSuccessListener = undefined
  stopFeatureFlagEvaluationTracking = undefined
}
